import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { KeyRound, Copy } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { datenzugangAPI } from '@/lib/api';

/* Datenzugang nur zum Lesen (19.10.2026, backend/datenzugang.py): Schlüssel für die Claude-Sitzung über die
   Anthropic-API, die nicht über den Browser ins CRM kommt. Nur lesen, nur freigegebene Bereiche, nie
   Gesundheitsdaten, läuft ab, jeder Abruf im Protokoll. Den Schlüssel zeigt das CRM genau einmal; er gehört in
   den Schlüsselbund des Mac, nicht in einen Chat. Verwalten darf nur der Inhaber (der Server prüft das). */

const KEYCHAIN = 'security add-generic-password -a "$USER" -s p1-crm-datenzugang -U -w';

const fehlerText = (e, standard) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : standard;
};

function zeit(iso) {
  if (!iso) return '–';
  let s = String(iso);
  if (/^\d{4}-\d{2}-\d{2}T/.test(s) && !/(Z|[+-]\d{2}:?\d{2})$/.test(s)) s += 'Z';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '–' : d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

async function kopieren(text, was) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${was} kopiert`);
  } catch {
    toast.error('Kopieren ging nicht. Bitte markieren und mit Cmd+C kopieren.');
  }
}

export default function DatenzugangKarte() {
  const [darf, setDarf] = useState(true);
  const [bereiche, setBereiche] = useState([]);
  const [maxTage, setMaxTage] = useState(90);
  const [zugaenge, setZugaenge] = useState([]);
  const [name, setName] = useState('Claude API-Sitzung');
  const [wahl, setWahl] = useState(['finanzen', 'kennzahlen']);
  const [tage, setTage] = useState(30);
  const [neu, setNeu] = useState(null);
  const [frage, setFrage] = useState(null);
  const [protokoll, setProtokoll] = useState({});
  const [laeuft, setLaeuft] = useState(false);

  const laden = useCallback(() => {
    Promise.all([datenzugangAPI.bereiche(), datenzugangAPI.liste()]).then(([b, l]) => {
      setBereiche(b.data.bereiche || []);
      setMaxTage(b.data.max_tage || 90);
      setZugaenge(l.data.zugaenge || []);
    }).catch((e) => { if (e?.response?.status === 403) setDarf(false); });
  }, []);
  useEffect(() => { laden(); }, [laden]);

  if (!darf) return null;

  const umschalten = (key) => setWahl((w) => (w.includes(key) ? w.filter((x) => x !== key) : [...w, key]));

  const anlegen = async () => {
    if (!wahl.length) { toast.error('Mindestens einen Bereich wählen'); return; }
    setLaeuft(true);
    try {
      const r = await datenzugangAPI.anlegen({ name: name.trim(), bereiche: wahl, tage: Number(tage) });
      setNeu(r.data);
      laden();
    } catch (e) {
      toast.error(fehlerText(e, 'Anlegen fehlgeschlagen'));
    } finally { setLaeuft(false); }
  };

  const widerrufen = async (id) => {
    try {
      await datenzugangAPI.widerrufen(id);
      toast.success('Zugang widerrufen');
      setFrage(null);
      laden();
    } catch (e) { toast.error(fehlerText(e, 'Widerrufen fehlgeschlagen')); }
  };

  const protokollZeigen = async (id) => {
    if (protokoll[id]) { setProtokoll((p) => ({ ...p, [id]: null })); return; }
    try {
      const r = await datenzugangAPI.protokoll(id);
      setProtokoll((p) => ({ ...p, [id]: r.data.abrufe || [] }));
    } catch (e) { toast.error(fehlerText(e, 'Protokoll nicht verfügbar')); }
  };

  const label = (key) => (bereiche.find((b) => b.key === key)?.label || key).split(':')[0];

  return (
    <Card className="border-border/60" data-testid="datenzugang-karte">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2"><KeyRound className="w-4 h-4" /> Datenzugang nur zum Lesen</CardTitle>
        <p className="text-xs text-muted-foreground">
          Für die Claude-Sitzung über die Anthropic-API (mit AVV). Der Zugang darf nur lesen, nur die gewählten Bereiche,
          nie Gesundheitsdaten. Er läuft ab und jeder Abruf steht im Protokoll.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {neu && (
          <div className="rounded-md border border-marke/40 bg-marke/5 p-3 space-y-2 text-sm" data-testid="datenzugang-neu">
            <p className="font-semibold">Schlüssel für „{neu.name}“. Er wird nur dieses eine Mal angezeigt.</p>
            <div className="flex gap-2 items-center">
              <code className="flex-1 min-w-0 break-all rounded bg-muted px-2 py-1 text-xs select-all">{neu.schluessel}</code>
              <Button size="sm" variant="outline" onClick={() => kopieren(neu.schluessel, 'Schlüssel')}><Copy className="w-3.5 h-3.5 mr-1" />Kopieren</Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Im Terminal diesen Befehl ausführen und den Schlüssel einfügen, wenn nach dem Passwort gefragt wird.
              Danach den Schlüssel nirgends sonst speichern und nie in einen Chat kopieren.
            </p>
            <div className="flex gap-2 items-center">
              <code className="flex-1 min-w-0 break-all rounded bg-muted px-2 py-1 text-xs select-all">{KEYCHAIN}</code>
              <Button size="sm" variant="outline" onClick={() => kopieren(KEYCHAIN, 'Befehl')}><Copy className="w-3.5 h-3.5 mr-1" />Kopieren</Button>
            </div>
            <Button size="sm" onClick={() => setNeu(null)}>Gespeichert, ausblenden</Button>
          </div>
        )}

        <div className="space-y-2">
          <p className="text-sm font-semibold">Neuer Zugang</p>
          <div className="grid gap-2 sm:grid-cols-[1fr_8rem]">
            <div className="space-y-1">
              <Label htmlFor="dz-name" className="text-xs">Name</Label>
              <Input id="dz-name" value={name} onChange={(e) => setName(e.target.value)} className="h-8" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="dz-tage" className="text-xs">Gültig (Tage)</Label>
              <Input id="dz-tage" type="number" min={1} max={maxTage} value={tage} onChange={(e) => setTage(e.target.value)} className="h-8" />
            </div>
          </div>
          <div className="flex flex-wrap gap-3 text-sm">
            {bereiche.map((b) => (
              <label key={b.key} className="flex items-center gap-1.5" title={b.label}>
                <input type="checkbox" checked={wahl.includes(b.key)} onChange={() => umschalten(b.key)} />
                {b.label.split(':')[0]}
              </label>
            ))}
          </div>
          <Button size="sm" disabled={laeuft} onClick={anlegen} data-testid="datenzugang-anlegen">Zugang anlegen</Button>
        </div>

        <div className="space-y-2">
          {zugaenge.length === 0 && <p className="text-xs text-muted-foreground">Noch kein Zugang angelegt.</p>}
          {zugaenge.map((z) => {
            const aktiv = z.aktiv && !z.widerrufen_am && new Date(z.ablauf) > new Date();
            return (
              <div key={z.id} className="rounded-md border border-border/60 p-2 text-sm space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{z.name}</span>
                  <code className="text-xs text-muted-foreground">{z.praefix}…</code>
                  <Badge variant="outline" className={aktiv ? 'text-emerald-600' : 'text-muted-foreground'}>
                    {z.widerrufen_am ? 'widerrufen' : aktiv ? 'aktiv' : 'abgelaufen'}
                  </Badge>
                  {(z.bereiche || []).map((b) => <Badge key={b} variant="secondary" className="text-[10px]">{label(b)}</Badge>)}
                </div>
                <p className="text-xs text-muted-foreground">
                  Gültig bis {zeit(z.ablauf)} · zuletzt genutzt {zeit(z.zuletzt_genutzt)} · {z.nutzungen || 0} Abrufe
                  {z.widerrufen_am ? ` · widerrufen ${zeit(z.widerrufen_am)}` : ''}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="ghost" className="h-7" onClick={() => protokollZeigen(z.id)}>
                    {protokoll[z.id] ? 'Protokoll ausblenden' : 'Protokoll'}
                  </Button>
                  {aktiv && frage !== z.id && (
                    <Button size="sm" variant="ghost" className="h-7 text-red-500" onClick={() => setFrage(z.id)}>Widerrufen</Button>
                  )}
                  {frage === z.id && (
                    <span className="flex items-center gap-2 text-xs">
                      Wirklich widerrufen? Der Schlüssel gilt dann sofort nicht mehr.
                      <Button size="sm" variant="destructive" className="h-7" onClick={() => widerrufen(z.id)}>Ja, widerrufen</Button>
                      <Button size="sm" variant="ghost" className="h-7" onClick={() => setFrage(null)}>Abbrechen</Button>
                    </span>
                  )}
                </div>
                {protokoll[z.id] && (
                  <div className="max-h-48 overflow-y-auto rounded bg-muted/40 p-2 text-xs font-mono">
                    {protokoll[z.id].length === 0 ? 'Noch keine Abrufe.' : protokoll[z.id].map((a) => (
                      <div key={a.id}>{zeit(a.am)} · {a.methode} {a.pfad}</div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
